import { useQueryClient } from '@tanstack/react-query';
import {
  approveCellRulePanel,
  createCellPanel,
  createCellRuleRevision,
  deleteCellRulePanel,
  updateCellPanel,
  updateCellRule,
} from 'api/cellRules';
import { filter, map, omit, reject } from 'es-toolkit/compat';
import { CellRulePanelWithRules, globalStudyId } from 'interfaces/cellRule';
import { enqueueSnackbar } from 'notistack';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { getCellRulePanelsKey } from './useCellRulePanels';
import { cellRuleQueryKey } from './useCellRules';

export const useUpdateCellRuleMutation = () => {
  const queryClient = useQueryClient();

  return useMutationWithErrorSnackbar({
    mutationDescription: 'Update Cell Rule',
    showRetryButton: false,
    mutationFn: updateCellRule,
    onMutate: (variables) => {
      const key = [...cellRuleQueryKey, variables.panelId, variables.studyId ?? globalStudyId];

      queryClient.setQueryData(key, (oldData: CellRulePanelWithRules) => {
        const newCellRules = reject(oldData.rules, {
          cellTypeId: variables.updateModel.cellTypeId,
          stainTypeId: variables.updateModel.stainTypeId,
        });

        newCellRules.push(variables.updateModel);

        return {
          ...oldData,
          rules: newCellRules,
        };
      });
    },
    onSuccess() {
      enqueueSnackbar('Cell Rule Updated', {
        variant: 'success',
      });
    },
    onSettled: (data, error, variables) => {
      const key = [...cellRuleQueryKey, variables.panelId, variables.studyId ?? globalStudyId];

      queryClient.invalidateQueries(key);
    },
  });
};
export const cellRuleRevisionErrorMessage = (error: {
  response?: { body?: { detail?: unknown; message?: unknown } };
  message?: string;
}) => {
  const detail = error.response?.body?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) return detail.map((item) => String(item)).join(' ');
  const message = error.response?.body?.message;
  if (typeof message === 'string' && message) return message;
  return error.message || 'Could not save the cell rules revision.';
};

export const useCreateCellRuleRevisionMutation = () => {
  const queryClient = useQueryClient();

  return useMutationWithErrorSnackbar({
    mutationDescription: 'Save cell rules revision',
    showRetryButton: false,
    mutationFn: createCellRuleRevision,
    errorMessageGetter: cellRuleRevisionErrorMessage,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(['study', variables.studyId]);
      enqueueSnackbar('Cell rules revision saved', { variant: 'success' });
    },
  });
};

export const useCreateCellRulePanelMutation = () => {
  const queryClient = useQueryClient();

  return useMutationWithErrorSnackbar({
    mutationDescription: 'Create Cell Rule Panel',
    showRetryButton: false,
    mutationFn: createCellPanel,
    onSuccess(data, variables) {
      const key = getCellRulePanelsKey(variables.studyId);

      queryClient.setQueryData(key, (oldData: CellRulePanelWithRules[]) => {
        return [...oldData, data];
      });
      enqueueSnackbar('Cell Rule Panel Created', {
        variant: 'success',
      });
    },
    onSettled: (data, error, variables) => {
      const key = getCellRulePanelsKey(variables.studyId);

      queryClient.invalidateQueries(key);
    },
  });
};

export const useUpdateCellRulePanelMutation = () => {
  const queryClient = useQueryClient();

  return useMutationWithErrorSnackbar({
    mutationDescription: 'Update Cell Rule Panel',
    showRetryButton: false,
    mutationFn: updateCellPanel,
    onSuccess: (data, variables) => {
      const key = getCellRulePanelsKey(variables.studyId);
      queryClient.setQueryData(key, (oldData: CellRulePanelWithRules[]) => {
        return map(oldData, (panel) => {
          return panel.id === variables.panelId ? data : panel;
        });
      });
      enqueueSnackbar('Cell Rule Panel Updated', {
        variant: 'success',
      });
    },
    onSettled: (data, error, variables) => {
      const cellPanelsKey = getCellRulePanelsKey(variables?.studyId);
      const panelRulesKey = [...cellRuleQueryKey, variables?.panelId, variables?.studyId];

      queryClient.invalidateQueries(cellPanelsKey);
      queryClient.invalidateQueries(panelRulesKey);
    },
  });
};

export const useDeleteCellRulePanelMutation = () => {
  const queryClient = useQueryClient();

  return useMutationWithErrorSnackbar({
    mutationDescription: 'Delete Cell Rule Panel',
    showRetryButton: false,
    mutationFn: deleteCellRulePanel,
    onSuccess: (data, variables) => {
      const key = getCellRulePanelsKey(variables?.studyId);
      queryClient.setQueryData(key, (oldData: CellRulePanelWithRules[]) => {
        return filter(oldData, (panel) => panel.id !== variables?.panelId);
      });
      const panelRulesKey = [...cellRuleQueryKey, variables?.panelId, variables?.studyId];
      if (data?.rules) {
        queryClient.setQueryData(panelRulesKey, data.rules);
      }
      enqueueSnackbar('Cell Rule Panel Deleted', {
        variant: 'success',
      });
    },
    onSettled: (data, error, variables) => {
      const cellPanelsKey = getCellRulePanelsKey(variables?.studyId);

      const panelRulesKey = [...cellRuleQueryKey, variables?.panelId, variables?.studyId];

      queryClient.invalidateQueries(cellPanelsKey);
      queryClient.invalidateQueries(panelRulesKey);
    },
  });
};

export const useApproveCellRulePanelMutation = () => {
  const queryClient = useQueryClient();

  return useMutationWithErrorSnackbar({
    mutationDescription: 'Approve Cell Rule Panel',
    showRetryButton: false,
    mutationFn: approveCellRulePanel,
    onMutate: (variables) => {
      const key = getCellRulePanelsKey(variables.studyId);

      queryClient.setQueryData(key, (oldData: CellRulePanelWithRules[]) => {
        return map(oldData, (panel) => {
          return panel.id === variables.panelId
            ? { ...panel, approvedAt: new Date().toISOString() }
            : omit(panel, 'approvedAt');
        });
      });
    },
    onSuccess: (data, variables) => {
      const key = getCellRulePanelsKey(variables.studyId);

      queryClient.setQueryData(key, (oldData: CellRulePanelWithRules[]) => {
        return map(oldData, (panel) => {
          return panel.id === variables.panelId
            ? { ...panel, approvedAt: new Date().toISOString() }
            : omit(panel, 'approvedAt');
        });
      });

      const snackbarMessage = variables.approved ? 'Cell Rule Panel Approved' : 'Cell Rule Panel Disapproved';
      enqueueSnackbar(snackbarMessage, {
        variant: 'success',
      });
    },
    onSettled: (data, error, variables) => {
      const cellPanelsKey = getCellRulePanelsKey(variables?.studyId);
      const panelRulesKey = [...cellRuleQueryKey, variables?.panelId, variables?.studyId];

      queryClient.invalidateQueries(cellPanelsKey);
      queryClient.invalidateQueries(panelRulesKey);
    },
  });
};
